import { EnumLeverageProductsIndication } from "src/api/client/extended-search.api.types";
import { IKeyFigure, ITags } from "src/api/server/extended-search.types";
import { EnumSortDirection } from "src/components/pages/search/components/sort-direction-button/sort-direction-button";
import { IKeyFigureValue, ISelectedRanking } from "src/components/pages/search/components/types";
import { SAVINGS_PLAN_TAG, TAG_IDS_IS_INVESTABLE } from "src/components/pages/search/constants";
import { commitSearchState, TViewMode, getSearchState, ISearchStore, setSearchStateSilently } from "src/components/pages/search/store/search-store";
import { saveViewMode } from "src/components/pages/search/store/search-view-local-storage";
import {
    getShouldSetIsInvestable,
    isKeyFigureValueDefault,
    getNewSelectedTags,
    isPopularSavingsPlanTagSelected,
} from "src/components/pages/search/store/utils";
import { ICountry } from "src/constants/locale";
import { ISortDirectionType } from "src/types/common";
import { EnumCountryKey } from "src/types/common-enum";

const setKeyFigures = (newKeyFigures: Array<IKeyFigureValue>) => {
    commitSearchState(current => ({ ...current, keyFigures: newKeyFigures }));
};

export const setQuery = (value: string) => {
    commitSearchState(current => ({ ...current, query: value }));
};

const setSortDirection = (value: ISortDirectionType) => {
    commitSearchState(current => ({ ...current, sortDirection: value }));
};

const setBrokersIds = (newBrokersIds: Array<string>) => {
    commitSearchState(current => ({ ...current, brokersIds: newBrokersIds }));
};

const setLeverageProductsIndication = (leverageProductsIndication: EnumLeverageProductsIndication) => {
    commitSearchState(current => ({ ...current, leverageProductsIndication }));
};

export const setView = (view: TViewMode) => {
    commitSearchState(current => ({ ...current, view }), { trackVisit: false });
    saveViewMode(view);
};

export const setSelectedFigures = (figureIds: ReadonlyArray<string>) => {
    setSearchStateSilently(current => ({ ...current, selectedFigures: figureIds }));
};

interface IHandleTags {
    isChecked: boolean;
    value: string;
    isPopular: boolean;
    tagsData: ITags;
}

export const handleTags = ({ isChecked, value, isPopular, tagsData }: IHandleTags) => {
    const shouldResetBrokersIds = SAVINGS_PLAN_TAG === value && isChecked === false;

    const newSelectedTags = getNewSelectedTags(isChecked, value, isPopular, tagsData, getSearchState().tags);
    const shouldIsInvestableBeSet = getShouldSetIsInvestable({ newSelectedTags, isInvestable: getSearchState().isInvestable });

    commitSearchState(current => ({
        ...current,
        tags: newSelectedTags,
        ...(shouldIsInvestableBeSet && { isInvestable: true }),
        ...(shouldResetBrokersIds && { brokersIds: [] }),
    }));
};

export const handleResetTags = () => {
    if (getSearchState().tags.length) {
        commitSearchState(current => ({ ...current, tags: [], brokersIds: [] }));
    }
};

interface IHandleKeyFigureChange {
    newKeyFigureValue: IKeyFigureValue;
    keyFiguresData: ReadonlyArray<IKeyFigure>;
}

const normalizeKeyFigureValue = (newKeyFigureValue: IKeyFigureValue, keyFiguresData: ReadonlyArray<IKeyFigure>): IKeyFigureValue => {
    const keyFigureDefaultValue = keyFiguresData.find(keyFigureValue => keyFigureValue.id === newKeyFigureValue.id);
    if (!keyFigureDefaultValue) {
        return newKeyFigureValue;
    }

    const defaultKeyFigureMinimum = keyFigureDefaultValue.steps[0];
    const defaultKeyFigureMaximum = keyFigureDefaultValue.steps.at(-1);
    return {
        ...newKeyFigureValue,
        min: newKeyFigureValue.min !== defaultKeyFigureMinimum ? newKeyFigureValue.min : undefined,
        max: newKeyFigureValue.max !== defaultKeyFigureMaximum ? newKeyFigureValue.max : undefined,
    };
};

const updateExistingKeyFigure = (newKeyFigureValue: IKeyFigureValue, valueOfNewKeyFigureInStore: IKeyFigureValue): void => {
    const isNewValueSameAsInStore =
        newKeyFigureValue.max === valueOfNewKeyFigureInStore.max && newKeyFigureValue.min === valueOfNewKeyFigureInStore.min;

    if (isNewValueSameAsInStore) {
        return;
    }

    const newKeyFigures = [...getSearchState().keyFigures];
    const indexOfNewKeyFigure = newKeyFigures.findIndex(keyFigure => keyFigure.id === newKeyFigureValue.id);
    newKeyFigures[indexOfNewKeyFigure] = newKeyFigureValue;

    setKeyFigures(newKeyFigures);
};

const addNewKeyFigure = (newKeyFigureValue: IKeyFigureValue): void => {
    setKeyFigures([...getSearchState().keyFigures, newKeyFigureValue]);
};

const removeKeyFigure = (newKeyFigureValue: IKeyFigureValue): void => {
    const newKeyFigures = [...getSearchState().keyFigures].filter(keyFigure => keyFigure.id !== newKeyFigureValue.id);
    setKeyFigures(newKeyFigures);
};

export const handleKeyFigureChange = ({ newKeyFigureValue, keyFiguresData }: IHandleKeyFigureChange) => {
    const valueOfNewKeyFigureInStore = getSearchState().keyFigures.find(keyFigure => keyFigure.id === newKeyFigureValue.id);
    const isDefaultValue = isKeyFigureValueDefault(newKeyFigureValue, keyFiguresData);

    if (isDefaultValue && valueOfNewKeyFigureInStore === undefined) {
        return;
    }

    if (isDefaultValue) {
        removeKeyFigure(newKeyFigureValue);
        return;
    }

    const normalizedKeyFigureValue = normalizeKeyFigureValue(newKeyFigureValue, keyFiguresData);

    if (valueOfNewKeyFigureInStore === undefined) {
        addNewKeyFigure(normalizedKeyFigureValue);
    } else {
        updateExistingKeyFigure(normalizedKeyFigureValue, valueOfNewKeyFigureInStore);
    }
};

export const handleResetKeyFigures = () => {
    setKeyFigures([]);
};

interface IHandleInvestable {
    isChecked: boolean;
}
export const handleIsInvestable = ({ isChecked }: IHandleInvestable) => {
    commitSearchState(current => {
        if (isChecked) {
            return { ...current, isInvestable: true };
        }

        return {
            ...current,
            isInvestable: false,
            isAtInvestable: false,
            isChInvestable: false,
            tags: current.tags.filter(tag => TAG_IDS_IS_INVESTABLE.every(tagId => tagId !== tag.value)),
            brokersIds: [],
        };
    });
};

export const handleIsInvestableInCountry = ({ isChecked, country }: IHandleInvestable & { country: ICountry }) => {
    if (country !== EnumCountryKey.AT && country !== EnumCountryKey.CH) {
        return;
    }

    commitSearchState(current => ({
        ...current,
        ...(country === EnumCountryKey.AT ? { isAtInvestable: isChecked } : { isChInvestable: isChecked }),
        ...(isChecked && { isInvestable: true }),
    }));
};

interface IHandleBrokersIds {
    isChecked: boolean;
    brokerId: string;
}
export const handleBrokersIds = ({ isChecked, brokerId }: IHandleBrokersIds) => {
    const selectedBrokersIds = isChecked ? [...getSearchState().brokersIds, brokerId] : getSearchState().brokersIds.filter(id => id !== brokerId);
    setBrokersIds(selectedBrokersIds);
};

interface IHandleResetSearch {
    sortDirection?: ISortDirectionType;
    selectedRanking?: ISelectedRanking;
    country: string;
}

export const handleResetSearch = ({ sortDirection, selectedRanking, country }: IHandleResetSearch) => {
    commitSearchState(current => ({
        ...current,
        query: "",
        selectedRanking: selectedRanking ?? current.selectedRanking,
        sortDirection: sortDirection ?? current.sortDirection,
        keyFigures: [],
        tags: [],
        brokersIds: [],
        isInvestable: country !== EnumCountryKey.INT,
        isAtInvestable: country === EnumCountryKey.AT,
        isChInvestable: country === EnumCountryKey.CH,
        leverageProductsIndication: EnumLeverageProductsIndication.AllWikifolios,
    }));
};

interface IHandleSearchInputField {
    relevanceRanking: ISelectedRanking;
}
export const handleSearchInputField = ({ relevanceRanking }: IHandleSearchInputField) => {
    commitSearchState(current => ({ ...current, sortDirection: relevanceRanking.sortDirection, selectedRanking: relevanceRanking }));
};

export const handleSortDirection = () => {
    const sortDirection =
        getSearchState().sortDirection === EnumSortDirection.PointingDown ? EnumSortDirection.PointingUp : EnumSortDirection.PointingDown;
    setSortDirection(sortDirection);
};

interface ISortingChange {
    sortByRankingId: string | null;
    sortDirection: ISortDirectionType;
}

const applySortingChange = ({ sortByRankingId, sortDirection }: ISortingChange) => {
    commitSearchState(current => ({
        ...current,
        sortDirection: sortByRankingId === null ? null : sortDirection,
        selectedRanking: { id: sortByRankingId, sortDirection },
    }));
};

export const handleSortingChange = (sortingChange: ISortingChange) => {
    if (sortingChange.sortByRankingId === getSearchState().selectedRanking?.id) {
        return;
    }

    applySortingChange(sortingChange);
};

export const handleTableSortingChange = (sortingChange: ISortingChange) => {
    applySortingChange(sortingChange);
};

interface IHandleLeverageProducts {
    leverageProductsIndication: EnumLeverageProductsIndication;
}
export const handleLeverageProducts = ({ leverageProductsIndication }: IHandleLeverageProducts) => {
    setLeverageProductsIndication(leverageProductsIndication);
};

export const handleResetPopularFilters = () => {
    commitSearchState(current => ({
        ...current,
        ...(isPopularSavingsPlanTagSelected(current.tags) && { brokersIds: [], isInvestable: true }),
        keyFigures: current.keyFigures.filter(keyFigure => !keyFigure.isPopular),
        tags: current.tags.filter(tag => !tag.isPopular),
        leverageProductsIndication: EnumLeverageProductsIndication.AllWikifolios,
    }));
};

export const setDefaultFilters = (ranking: ISelectedRanking, country: string) => {
    setSearchStateSilently(current => {
        const next: ISearchStore = {
            ...current,
            selectedRanking: ranking,
            sortDirection: ranking.sortDirection,
            leverageProductsIndication: EnumLeverageProductsIndication.AllWikifolios,
            isInvestable: country !== EnumCountryKey.INT,
        };

        switch (country) {
            case EnumCountryKey.AT:
                next.isChInvestable = false;
                break;
            case EnumCountryKey.CH:
                next.isAtInvestable = false;
                break;
            default:
                next.isChInvestable = false;
                next.isAtInvestable = false;
                break;
        }

        return next;
    });
};

export const setNewSearchStoreValues = (newSearchStore: ISearchStore) => {
    setSearchStateSilently(current => ({ ...current, ...newSearchStore }));
};
